import React, { useEffect, useMemo, useState, lazy, Suspense } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { theme } from './themes';
import 'bootstrap/dist/css/bootstrap.min.css';
import { galleryType } from './ApiServices/config';
import { Route, Routes, useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';

// Common components (not lazy loaded as they're used on every page)
import Header from './components/common/header/index';
import Footer from './components/common/footer/Footer';
import ScrollToTopOnNavigate from './components/common/ScrollToTopOnNavigate';
import PopUpNavigate from './components/PopUpNavigate';
import Undermaintenance from './pages/Undermaintenance';
import HashRedirect from './components/OldRoutes';
import LegacyRedirectPage from './components/LegacyRedirectPage';
import TempleLandingPage from './pages/koiskLogin/KioskLandingPage';
const KioskServices = lazy(() => import('./pages/koiskLogin/KioskServices'));
// Lazy load these page-level components instead of eagerly importing them
const EventTicketForm = lazy(() => import('./pages/Stepper/steps/eventTicketForm/EventTicketForm'));
const DailyPujaSchedule = lazy(() => import('./pages/home/components/dailySchedule/DailyPujaSchedule'));

// Lazy load all page components
const Home = lazy(() => import('./pages/home/Home'));
const ServiceNew = lazy(() => import('./pages/services/ServicesNew'));
const HomePageEvents = lazy(() => import('./pages/singlePageListViews/events/HomePageEvents'));
const HomePageNews = lazy(() => import('./pages/home/HomePageNews'));
const Registration = lazy(() => import('./pages/registration/Registration'));
const Login = lazy(() => import('./pages/login/Login'));
const Events = lazy(() => import('./pages/singlePageViews/event/Events'));
const EventDetails = lazy(() => import('./pages/eventDetails/EventDetails'));
const TempleHistory = lazy(() => import('./pages/templeHistory/TempleHistory'));
const TempleTrustees = lazy(() => import('./pages/templeTrustees/TempleTrustees'));
const TempleExecutiveBoard = lazy(() => import('./pages/templeExecutiveBoard/TempleExecutiveBoard'));
const CommitteeCoordinators = lazy(() => import('./pages/committeeCoordinators/CommitteeCoordinators'));
const ContactUs = lazy(() => import('./pages/contactUs/ContactUs'));
const RegistrationSuccess = lazy(() => import('./pages/registerationSuccess/RegistrationSuccess'));
const Profile = lazy(() => import('./pages/profile/Profile'));
const ForgotPassword = lazy(() => import('./pages/forgetPassword/ForgotPassword'));
const ForgotPasswordSent = lazy(() => import('./pages/forgetPassword/ForgotPasswordSent'));
const ResetPassword = lazy(() => import('./pages/resetPassword/ResetPassword'));
const ChangePassword = lazy(() => import('./pages/changePassword/ChangePassword'));
const Donations = lazy(() => import('./pages/donation/Donations'));
const Services = lazy(() => import('./pages/services/Services'));
const IndividualNews = lazy(() => import('./pages/individualNews/IndividualNews'));
const Gallery = lazy(() => import('./pages/gallery'));
const Photos = lazy(() => import('./pages/gallery/photos'));
const Videos = lazy(() => import('./pages/gallery/Videos'));
const ServicesAtTemple = lazy(() => import('./pages/serviceAtTemple/ServicesAtTemple'));
const FullPanchang = lazy(() => import('./pages/fullCalenderPanchang/fullCalenderPanchang'));
const ServicesOutsideTemple = lazy(() => import('./pages/serviceOutSideTemple/ServicesOutsideTemple'));
const Message = lazy(() => import('./pages/payment/Payment'));
const Cancellation = lazy(() => import('./pages/cancellation/Cancellation'));
const TheTemple = lazy(() => import('./pages/theTemple/TheTemple'));
const TempleMission = lazy(() => import('./pages/templeMission/TempleMission'));
const AllDonations = lazy(() => import('./pages/allDonations/AllDonations'));
const Hotels = lazy(() => import('./pages/hotels/Hotels'));
const LocationDirection = lazy(() => import('./pages/locationDirection/LocationDirection'));
const Faq = lazy(() => import('./pages/faq/Faq'));
const Guidelines = lazy(() => import('./pages/Guidelines/Guidelines'));
const SinglepageView = lazy(() => import('./pages/singlePageView/SinglepageView'));
const ExpressBookingServices = lazy(() => import('./pages/expressBooking/ExpressBookings'));
const PaymentReciept = lazy(() => import('./pages/paymentRecipt/PaymentReciept'));
const PrasadamServices = lazy(() => import('./pages/prasadamservices/PrasadamServices'));
const Catering = lazy(() => import('./pages/prasadamservices/Catering'));
const Cafeteria = lazy(() => import('./pages/prasadamservices/Cafeteria'));
const BalGokul = lazy(() => import('./pages/balGokul/BalGokul'));
const YouthTruth = lazy(() => import('./pages/youthTruth/YouthTruth'));
const MeraApne = lazy(() => import('./pages/meraApne/MeraApne'));
const Priests = lazy(() => import('./pages/preists/Priests'));
const Volunteer = lazy(() => import('./pages/volunteer/Volunteer'));
const Tvgallery = lazy(() => import('./pages/tvGallery/Tvgallery'));
const NewsLetterSuccess = lazy(() => import('./pages/newsLetterSuccess/NewsLetterSuccess'));
const Errorpage = lazy(() => import('./components/Errorpage'));
const PoojaSamagriList = lazy(() => import('./pages/poojaSamagri/PoojaSamagriList'));
const RecurringDonations = lazy(() => import('./pages/recurreingDonation/RecurringDonations'));
const Search = lazy(() => import('./pages/search/Search'));
const LandDonation = lazy(() => import('./components/home/donation/LandDonation'));
const StoneDonation = lazy(() => import('./components/home/donation/StoneDonation'));
const PbjShelters = lazy(() => import('./pages/pbjShelter/PbjShelters'));
const ReligiousBooks = lazy(() => import('./pages/religiousBooks/ReligiousBooks'));
const CalenderView = lazy(() => import('./pages/home/components/calendarView/CalenderView'));
const PaymentRecieptPage = lazy(() => import('./pages/payment/Payment'));
const Payment = lazy(() => import('./pages/checkout/Checkout'));
const Requests = lazy(() => import('./pages/requests/request'));
const KioskLogin = lazy(() => import('./pages/koiskLogin/Home'));
const KioskWithSearch = lazy(() => import('./pages/koiskLogin/HomeSearch'));
const KioskExpressDonation = lazy(() => import('./pages/kioskExpressDonation/ExpressDonation'));
const KioskRecipt = lazy(() => import('./pages/kioskExpressDonation/recipt'));
const Report = lazy(() => import('./pages/reports/reports'));
const Livestreaming = lazy(() => import('./pages/livestreaming/Livestreaming'));
const PrasadamPolicy = lazy(() => import('./pages/prasadamPolicy/prasadamPolicy'));
const PosLanding = lazy(() => import('./pages/pos/PosLanding'));
const PosServices = lazy(() => import('./pages/pos/posServices'));
const ClassPaymentReceipt = lazy(() => import('./pages/lotusacademy/AcademyReciept'));
const HallBooking = lazy(() => import('./pages/rentals/HallBooking'));
const HallBookingDetails = lazy(() => import('./pages/rentals/HallBookingDetails'));
const BookingStepper = lazy(() => import('./pages/Stepper/stepper'));
const KioskLanding = lazy(() => import('./pages/koiskLogin/KioskLanding'));
const KioskServiceDetail = lazy(() => import('./pages/koiskLogin/KioskServiceDetail')); 
const KioskOrderBooking = lazy(() => import('./pages/koiskLogin/KioskOrderBooking'));
const KioskServiceSearch = lazy(() => import('./pages/koiskLogin/KioskServiceSearch'));
const KioskPayment = lazy(() => import('./pages/koiskLogin/KioskPayment'));
const PaymentRedirect = lazy(() => import('./pages/payment/PaymentRedirect'));
const Lotusacademy = lazy(() => import('./pages/lotusacademy/Lotusacademy'));
const PersonalServices = lazy(() => import('./pages/personalServices/PersonalServices'));
const ArticleAndBylaws = lazy(() => import('./pages/articlesAndBylaws/ArticlesAndBylaws'));
const Activities = lazy(() => import('./pages/singlePageListViews/activities/Activities'));
const Careers = lazy(() => import('./pages/careers/careers'));
const YearEndReport = lazy(() => import('./pages/yearendreport/yearendreport'));
const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;

// Debounced click tracking - only track meaningful clicks (buttons, links, inputs)
const TRACKABLE_TAGS = new Set(['A', 'BUTTON', 'INPUT', 'SELECT']);
let clickTimeout = null;
const trackClickEvent = (event) => {
  const target = event.target.closest('a, button, input, select, [role="button"]');
  if (!target || (!TRACKABLE_TAGS.has(target.tagName) && !target.getAttribute('role'))) return;
  if (clickTimeout) clearTimeout(clickTimeout);
  clickTimeout = setTimeout(() => {
    ReactGA.event('click', {
      event_category: target.tagName,
      event_label: target.innerText?.slice(0, 50) || target.id || 'Unnamed'
    });
  }, 300);
};

function App() {
  const location = useLocation();
  const [gaReady, setGaReady] = useState(!measurementId);

  useEffect(() => {
    if (!measurementId) return undefined;
    const run = () => {
      ReactGA.initialize(measurementId);
      setGaReady(true);
    };
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(run, { timeout: 3000 });
      return () => window.cancelIdleCallback(id);
    }
    const t = window.setTimeout(run, 0);
    return () => window.clearTimeout(t);
  }, [measurementId]);

  // Memoize paths without header/footer to avoid recalculation on every render
  const pathsWithoutLayout = useMemo(
    () => [
      '/tvgallery',
      '/kiosk/expressDonation',
      '/kiosk/loginSearch',
      '/kiosk/login',
      '/kiosk/home',
      '/kiosk/services',
      '/kiosk/landing',
      '/kiosk/serviceDetail',
      '/kiosk/orderBooking',
      '/kiosk/serviceSearch',
      '/kiosk/payment',
      '/poslanding',
      '/poshome',
      '/posdonation',
      '/posservices'
    ],
    []
  );

  // Check if current path should show header/footer
  const shouldShowLayout = useMemo(() => !pathsWithoutLayout.includes(location.pathname), [location.pathname, pathsWithoutLayout]);

  // Track page views on location change (after GA init is deferred past first paint)
  useEffect(() => {
    if (!measurementId || !gaReady) return;
    ReactGA.send({
      hitType: 'pageview',
      page: location.pathname + location.search
    });
  }, [location.pathname, location.search, gaReady]);

  // Setup click tracking only once
  useEffect(() => {
    const handleDocumentClick = (event) => trackClickEvent(event);
    document.addEventListener('click', handleDocumentClick);

    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []); // Empty dependency array - only run once

  // Memoize theme to prevent recreation
  const appTheme = useMemo(() => theme(), []);

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <React.Fragment>
        <HashRedirect />
        <ScrollToTopOnNavigate />
        <PopUpNavigate />
        {shouldShowLayout && <Header />}

        <Suspense fallback={<div style={{ minHeight: '100vh' }}>Loading...</div>}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/services/:slug" element={<Services />} />
            <Route path="/services/donation" element={<AllDonations />} />
            <Route path="/outsidetemple" element={<ServicesOutsideTemple />} />
            <Route path="/servicesattemple" element={<ServicesAtTemple />} />
            <Route path="/donation" element={<AllDonations />} />
            <Route path="/donation/:donationcategory" element={<Donations />} />

            <Route path="/expressbookings" element={<ExpressBookingServices />} />
            {/* <Route path="/#/services/express" element={<ExpressBookingServices />} /> */}

            <Route path="/fullpanchang" element={<FullPanchang />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgotpassword" element={<ForgotPassword />} />
            <Route path="/forgotpasswordsent" element={<ForgotPasswordSent />} />
            <Route path="/reset-password/:email_reset_token" element={<ResetPassword />} />
            <Route path="/changepassword" element={<ChangePassword />} />
            <Route path="/registration" element={<Registration />} />
            <Route path="/registrationsuccess" element={<RegistrationSuccess />} />
            <Route path="/newsLettersuccess" element={<NewsLetterSuccess />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/report" element={<Report />} />
            <Route path="/events" element={<HomePageEvents />} />
            <Route path="/events/calendar" element={<CalenderView />} />
            <Route path="/events/:slug" element={<HomePageEvents />} />
            <Route path="/activities" element={<Activities />} />
            <Route path="/activities/:slug" element={<Activities />} />
            <Route path="/news" element={<HomePageNews />} />
            <Route path="/knowmoreevents/:slug" element={<Events />} />
            <Route path="/knowmorenews/:slug" element={<IndividualNews />} />
            <Route path="/eventdetails" element={<EventDetails />} />
            <Route path="/servicerequest" element={<Requests />} />
            <Route path="/personalservices" element={<PersonalServices />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/yearendreport" element={<YearEndReport />} />

            <Route path="/kiosk">
              <Route path="login" element={<KioskLogin />} />
              <Route path="loginSearch" element={<KioskWithSearch />} />
              <Route path="expressDonation" element={<KioskExpressDonation />} />
              <Route path="recipt" element={<KioskRecipt />} />
              <Route path="services" element={<KioskServices />} />
              <Route path="home" element={<TempleLandingPage />} />
              <Route path="landing" element={<KioskLanding />} />
              <Route path="serviceDetail" element={<KioskServiceDetail />} />
              <Route path="orderBooking" element={<KioskOrderBooking />} />
              <Route path="serviceSearch" element={<KioskServiceSearch />} />
              <Route path="payment" element={<KioskPayment />} />
            </Route>

            <Route path="/gallery">
              <Route path="photos" element={<Gallery type={galleryType.photo} key={galleryType.photo} />} />
              <Route path="photos/:slug" element={<Photos />} />
              <Route path="videos" element={<Gallery type={galleryType.video} key={galleryType.video} />} />
              <Route path="videos/:slug" element={<Videos />} />
            </Route>

            <Route path="/templehistory" element={<TempleHistory />} />
            <Route path="/management" element={<TempleTrustees />} />
            <Route path="/executiveboard" element={<TempleExecutiveBoard />} />
            <Route path="/coordinators" element={<CommitteeCoordinators />} />
            <Route path="/contactus" element={<ContactUs />} />
            <Route path="/payment-status" element={<Message />} />
            <Route path="/checkout" element={<Payment />} />
            <Route path="/payment" element={<PaymentRecieptPage />} />
            <Route path="/servicesnew" element={<ServiceNew></ServiceNew>} />

            <Route path="/cancellation/:bookingId" element={<Cancellation />} />
            <Route path="/thetemple" element={<TheTemple />} />
            <Route path="/mission" element={<TempleMission />} />
            <Route path="/hotels" element={<Hotels />} />
            <Route path="/location" element={<LocationDirection />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/guidelines" element={<Guidelines />} />
            <Route path="/prasadampolicy" element={<PrasadamPolicy />} />
            <Route path="/services/:category/:slug" element={<SinglepageView />} />
            <Route path="/paymentreciept" element={<PaymentReciept />} />
            <Route path="/prasadamservices" element={<PrasadamServices />} />
            <Route path="/catering" element={<Catering />} />
            <Route path="/cafeteria" element={<Cafeteria />} />
            <Route path="/balgokul" element={<BalGokul />} />
            <Route path="/youthtruth" element={<YouthTruth />} />
            <Route path="/meraapne" element={<MeraApne />} />
            <Route path="/errorpage" element={<Errorpage />} />
            <Route path="/priests" element={<Priests />} />
            <Route path="/volunteer" element={<Volunteer />} />
            <Route path="/tvgallery" element={<Tvgallery />} />
            <Route path="/poojasamagri" element={<PoojaSamagriList />} />
            <Route path="/search" element={<Search />} />
            <Route path="/recurringdonations" element={<RecurringDonations />} />
            <Route path="/landdonation" element={<LandDonation />} />
            <Route path="/stonedonation" element={<StoneDonation />} />
            <Route path="/pbj" element={<PbjShelters />} />
            {/* <Route path="/calenderview" element={<CalenderView />} /> */}
            <Route path="/religioustexts" element={<ReligiousBooks />} />
            <Route path="/livestreaming" element={<Livestreaming />} />
            <Route path="/poslanding" element={<PosLanding />} />
            <Route path="/posservices" element={<PosServices />} />
            <Route path="/lotusacademy" element={<Lotusacademy />} />
            <Route path="/academyRecipt" element={<ClassPaymentReceipt />} />
            <Route path="/hallbooking" element={<HallBooking />} />
            <Route path="/articlesandbylaws" element={<ArticleAndBylaws />} />
            <Route path="/hallbookingdetails" element={<HallBookingDetails />} />
            <Route path="/:servicetype/:slug" element={<BookingStepper />} />
            <Route path="/eventticketform" element={<EventTicketForm />} />
            <Route path="/payment/redirect" element={<PaymentRedirect />} />
            <Route path="/dailypujaschedule" element={<DailyPujaSchedule />} />
            <Route path="/undermaintenance" element={<Undermaintenance />} />
            <Route path="/legacy-redirect" element={<LegacyRedirectPage />} />
            <Route path="*" element={<LegacyRedirectPage />} />
          </Routes>
        </Suspense>
        {shouldShowLayout && <Footer />}
      </React.Fragment>
    </ThemeProvider>
  );
}

export default App;
